import React, { useEffect, useState } from 'react';
import { useLocalStorage, writeStorage } from '@rehooks/local-storage';
import CookieConsentModal from 'components/CookieConsentModal';
import { COOKIE_ACCEPTED, COOKIE_DECLINED, localStorageKeys } from 'constants';
import {
  discardPendingConversions,
  flushPendingConversions,
  trackGtagPageView,
} from 'utils/analytics';
import { setOpenAIPixelConsent } from 'utils/openaiPixel';
import { isPrerendering } from 'utils/prerender';

import { GlobalStyle } from './GlobalStyles';
import Providers from './Providers';
import Routes from './Routes';

const App = () => {
  const [cookieConsent] = useLocalStorage(localStorageKeys.cookieConsent);

  // The cookie banner is only shown after the app runs in a real browser.
  // Prerendered HTML (scripts/prerender.js) and React's first hydration render
  // both keep it hidden, so the markup matches regardless of the visitor's
  // stored choice; the effect then reveals it only for visitors without one.
  // Without this, visitors who had already decided saw the banner from the
  // static HTML until hydration removed it.
  const [showConsentUi, setShowConsentUi] = useState(false);
  useEffect(() => {
    if (!isPrerendering()) setShowConsentUi(true);
  }, []);

  useEffect(() => {
    if (cookieConsent === COOKIE_ACCEPTED) {
      setOpenAIPixelConsent(true);
    }

    // If the user has already accepted cookies, set consent to granted
    if (cookieConsent === COOKIE_ACCEPTED && window.gtag) {
      // Update consent to 'granted' for all parameters
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });

      // Track page view now that consent is granted
      trackGtagPageView();
    }
  }, [cookieConsent]);

  const handleAcceptCookies = () => {
    writeStorage(localStorageKeys.cookieConsent, COOKIE_ACCEPTED);
    setOpenAIPixelConsent(true);

    // Update consent to 'granted' for all parameters when cookies are accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });

      // Track page view now that consent is granted
      trackGtagPageView();

      // e.g. a demo booked before the banner was answered
      flushPendingConversions();
    }
  };

  const handleDeclineCookies = () => {
    writeStorage(localStorageKeys.cookieConsent, COOKIE_DECLINED);
    setOpenAIPixelConsent(false);
    discardPendingConversions();

    // Ensure consent remains denied if user declines
    if (window.gtag) {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        analytics_storage: 'denied',
      });
    }
  };

  return (
    <Providers>
      <GlobalStyle />
      <Routes />
      <CookieConsentModal
        isVisible={showConsentUi && !cookieConsent}
        onAccept={handleAcceptCookies}
        onDecline={handleDeclineCookies}
      />
    </Providers>
  );
};

export default App;
